import { useState, useEffect } from "react";
import { useApp } from "@/App";
import { motion } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Lock, LogOut, Plus, Trash2, CalendarDays, List, ChevronLeft, ChevronRight, X, Car, Download
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from "date-fns";
import { cs } from "date-fns/locale";
import HandoverPanel from "@/components/HandoverPanel";

export default function AdminPanel({ onClose }) {
  const { API, packages } = useApp();
  const [token, setToken] = useState(null);
  const [location, setLocation] = useState("");
  const [username, setUsername] = useState("");
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [bookings, setBookings] = useState([]);
  const [view, setView] = useState("calendar");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBooking, setNewBooking] = useState({
    customer_name: "", customer_phone: "", customer_email: "",
    vehicle_type: "sedan", package_name: "", date: "", time: "", total_price: 0, notes: ""
  });

  const fetchBookings = async (tok) => {
    try {
      const res = await axios.get(`${API}/admin/bookings`, { params: { token: tok || token, location } });
      setBookings(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleLogin = async () => {
    try {
      const res = await axios.post(`${API}/admin/login`, loginForm);
      setToken(res.data.token);
      setLocation(res.data.location);
      setUsername(res.data.username);
      toast.success(`Přihlášen jako ${res.data.username}`);
      fetchBookings(res.data.token);
    } catch {
      toast.error("Neplatné přihlašovací údaje");
    }
  };

  useEffect(() => {
    if (token) fetchBookings();
  }, [token, location]);

  const handleAddBooking = async () => {
    try {
      await axios.post(`${API}/admin/bookings?token=${token}`, {
        ...newBooking,
        location,
        package_id: "",
        addon_ids: [],
        addon_names: []
      });
      toast.success("Rezervace přidána");
      setShowAddForm(false);
      setNewBooking({ customer_name: "", customer_phone: "", customer_email: "", vehicle_type: "sedan", package_name: "", date: "", time: "", total_price: 0, notes: "" });
      fetchBookings();
    } catch {
      toast.error("Chyba při přidávání rezervace");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Opravdu smazat tuto rezervaci?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}?token=${token}`);
      toast.success("Rezervace smazána");
      fetchBookings();
    } catch {
      toast.error("Chyba při mazání");
    }
  };

  const bookingsForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return bookings.filter(b => b.date === dateStr);
  };

  const todayBookings = bookingsForDate(selectedDate);
  const locationName = location === "cestlice" ? "Čestlice" : "Butovice";

  // Login screen
  if (!token) {
    return (
      <div className="fixed inset-0 z-[70] bg-[#050505] flex items-center justify-center" data-testid="admin-login">
        <button onClick={onClose} className="absolute top-6 right-6 text-zinc-500 hover:text-white">
          <X className="w-6 h-6" />
        </button>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm px-6"
        >
          <div className="flex items-center gap-3 mb-8">
            <Lock className="w-5 h-5 text-[#39FF14]" />
            <h2 className="text-xl font-bold uppercase tracking-wider">Admin</h2>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-2 block">Uživatel</Label>
              <Input
                value={loginForm.username}
                onChange={(e) => setLoginForm(p => ({...p, username: e.target.value}))}
                className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-12"
                placeholder="cestlice / butovice"
                data-testid="admin-username"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-2 block">Heslo</Label>
              <Input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm(p => ({...p, password: e.target.value}))}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-12"
                placeholder="Heslo"
                data-testid="admin-password"
              />
            </div>
            <button
              onClick={handleLogin}
              className="w-full bg-[#39FF14] text-black py-3 text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors duration-300"
              data-testid="admin-login-btn"
            >
              Přihlásit
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Pavel sees handover panel
  if (username === "pavel") {
    return (
      <div className="fixed inset-0 z-[70] bg-[#050505] overflow-y-auto" data-testid="admin-dashboard">
        <div className="sticky top-0 z-10 bg-[#050505] border-b border-white/5 px-4 md:px-8 py-4 flex items-center justify-between">
          <h1 className="text-lg font-bold uppercase tracking-wider">
            Car <span className="text-[#39FF14]">Spa</span>
            <span className="text-xs text-zinc-500 ml-2 font-normal">Převzetí vozidla</span>
          </h1>
          <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
        <div className="max-w-2xl mx-auto py-6">
          <HandoverPanel token={token} onClose={onClose} />
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="fixed inset-0 z-[70] bg-[#050505] overflow-y-auto" data-testid="admin-dashboard">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#050505] border-b border-white/5 px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-bold uppercase tracking-wider">
            Car <span className="text-[#39FF14]">Spa</span>
            <span className="text-xs text-zinc-500 ml-2 font-normal">{locationName}</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              const url = `${API}/admin/export-csv?token=${token}&location=${location}`;
              window.open(url, '_blank');
              toast.success("CSV export stažen");
            }}
            className="p-2 text-zinc-400 hover:text-[#39FF14] transition-colors"
            title="Stáhnout CSV"
            data-testid="admin-export-csv"
          >
            <Download className="w-5 h-5" />
          </button>
          <button
            onClick={() => setView(view === "calendar" ? "list" : "calendar")}
            className="p-2 text-zinc-400 hover:text-white transition-colors"
            data-testid="admin-toggle-view"
          >
            {view === "calendar" ? <List className="w-5 h-5" /> : <CalendarDays className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 bg-[#39FF14] text-black px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors"
            data-testid="admin-add-btn"
          >
            <Plus className="w-4 h-4" /> Přidat
          </button>
          <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        {/* Calendar view */}
        {view === "calendar" && (
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8">
            <div className="bg-[#0A0A0A] border border-white/5 p-4 calendar-dark">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                locale={cs}
                className="text-white"
                modifiers={{
                  hasBooking: bookings.map(b => parseISO(b.date))
                }}
                modifiersStyles={{
                  hasBooking: { borderBottom: "2px solid #39FF14" }
                }}
                data-testid="admin-calendar"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">
                  {format(selectedDate, "d. MMMM yyyy", { locale: cs })}
                </h2>
                <span className="text-xs text-[#39FF14] font-bold">
                  {todayBookings.length} rezervací
                </span>
              </div>

              {todayBookings.length === 0 ? (
                <div className="bg-[#0A0A0A] border border-white/5 p-8 text-center">
                  <p className="text-zinc-500 text-sm">Žádné rezervace na tento den</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {todayBookings.sort((a,b) => a.time.localeCompare(b.time)).map((b) => (
                    <div key={b.id} className="bg-[#0A0A0A] border border-white/5 p-4 flex items-start justify-between group hover:border-[#39FF14]/20 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-[#39FF14] font-bold text-lg">{b.time}</span>
                          <span className="text-white font-bold">{b.customer_name}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                          <span>{b.package_name}</span>
                          <span>{b.vehicle_type} {b.vehicle_brand}</span>
                          <span>{b.customer_phone}</span>
                          {b.customer_email && <span>{b.customer_email}</span>}
                        </div>
                        {b.addon_names?.length > 0 && (
                          <p className="text-xs text-zinc-600 mt-1">+ {b.addon_names.join(", ")}</p>
                        )}
                        {b.notes && <p className="text-xs text-zinc-600 mt-1 italic">{b.notes}</p>}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm font-bold text-[#39FF14]">{(b.final_price || b.total_price).toLocaleString()} Kč</span>
                          {b.discount_code && <span className="text-xs text-zinc-600">({b.discount_code} -{b.discount_amount} Kč)</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(b.id)}
                        className="p-2 text-zinc-700 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                        data-testid={`admin-delete-${b.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* List view */}
        {view === "list" && (
          <div className="space-y-2">
            <h2 className="text-lg font-bold mb-4">Všechny rezervace</h2>
            {bookings.length === 0 ? (
              <p className="text-zinc-500 text-sm">Žádné rezervace</p>
            ) : (
              bookings.sort((a,b) => b.date.localeCompare(a.date) || b.time.localeCompare(a.time)).map((b) => (
                <div key={b.id} className="bg-[#0A0A0A] border border-white/5 p-4 flex items-center justify-between group hover:border-[#39FF14]/20 transition-colors">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="text-center min-w-[60px]">
                      <p className="text-xs text-zinc-500">{b.date}</p>
                      <p className="text-[#39FF14] font-bold">{b.time}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{b.customer_name}</p>
                      <p className="text-xs text-zinc-500 truncate">{b.package_name} — {b.customer_phone}</p>
                    </div>
                    <span className="text-sm font-bold text-[#39FF14] whitespace-nowrap">{(b.final_price || b.total_price).toLocaleString()} Kč</span>
                  </div>
                  <button
                    onClick={() => handleDelete(b.id)}
                    className="p-2 ml-2 text-zinc-700 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Add booking modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0A0A0A] border border-white/10 w-full max-w-md max-h-[90vh] overflow-y-auto p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold uppercase tracking-wider">Nová rezervace</h3>
              <button onClick={() => setShowAddForm(false)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">Jméno *</Label>
                <Input value={newBooking.customer_name} onChange={(e) => setNewBooking(p=>({...p, customer_name: e.target.value}))}
                  className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-10" data-testid="admin-input-name" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">Telefon *</Label>
                <Input value={newBooking.customer_phone} onChange={(e) => setNewBooking(p=>({...p, customer_phone: e.target.value}))}
                  className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-10" data-testid="admin-input-phone" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">E-mail</Label>
                <Input value={newBooking.customer_email} onChange={(e) => setNewBooking(p=>({...p, customer_email: e.target.value}))}
                  className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-10" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">Balíček *</Label>
                <select value={newBooking.package_name} onChange={(e) => setNewBooking(p=>({...p, package_name: e.target.value}))}
                  className="w-full bg-white/5 border border-white/10 focus:border-[#39FF14] text-white rounded-none h-10 px-3 text-sm" data-testid="admin-input-package">
                  <option value="" className="bg-[#0A0A0A]">Vyberte...</option>
                  {packages.map(pkg => (
                    <option key={pkg.id} value={pkg.name} className="bg-[#0A0A0A]">{pkg.name} — {pkg.price} Kč</option>
                  ))}
                  <option value="Vlastní" className="bg-[#0A0A0A]">Vlastní</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">Datum *</Label>
                  <Input type="date" value={newBooking.date} onChange={(e) => setNewBooking(p=>({...p, date: e.target.value}))}
                    className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-10" data-testid="admin-input-date" />
                </div>
                <div>
                  <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">Čas *</Label>
                  <Input type="time" value={newBooking.time} onChange={(e) => setNewBooking(p=>({...p, time: e.target.value}))}
                    className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-10" data-testid="admin-input-time" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">Cena (Kč)</Label>
                <Input type="number" value={newBooking.total_price} onChange={(e) => setNewBooking(p=>({...p, total_price: parseInt(e.target.value) || 0}))}
                  className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-10" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">Poznámka</Label>
                <textarea value={newBooking.notes} onChange={(e) => setNewBooking(p=>({...p, notes: e.target.value}))}
                  className="w-full bg-white/5 border border-white/10 focus:border-[#39FF14] text-white rounded-none p-3 h-20 text-sm focus:outline-none" />
              </div>
              <button
                onClick={handleAddBooking}
                disabled={!newBooking.customer_name || !newBooking.customer_phone || !newBooking.package_name || !newBooking.date || !newBooking.time}
                className="w-full bg-[#39FF14] text-black py-3 text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors disabled:bg-white/10 disabled:text-zinc-600 disabled:cursor-not-allowed"
                data-testid="admin-submit-booking"
              >
                Vytvořit rezervaci
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
